import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "./store/useAuthStore.ts";
import { getMe } from "./api/auth.ts";

import LoginPage from "./pages/LoginPage.tsx";
import DashboardPage from "./pages/DashboardPage.tsx";
import ScreeningPage from "./pages/ScreeningPage.tsx";
import ExtractionPage from "./pages/ExtractionPage.tsx";
import AdminPage from "./pages/AdminPage.tsx";
import PublicViewPage from "./pages/PublicViewPage.tsx";
import ImportPage from "./pages/ImportPage.tsx";
import RiskOfBiasPage from "./pages/RiskOfBiasPage.tsx";
import ForceChangePasswordPage from "./pages/ForceChangePasswordPage.tsx";
import Layout from "./components/Layout.tsx";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();
  if (isLoading) return <div className="flex items-center justify-center h-screen text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Blocks every route behind this gate (including the sidebar/nav in Layout)
  // until the forced password change is done. The backend enforces the same
  // rule on every API call, so this is UX, not the security boundary.
  if (user.mustChangePassword) return <ForceChangePasswordPage />;
  return <>{children}</>;
}

function RequireRole({ children, roles }: { children: React.ReactNode; roles: string[] }) {
  const { user } = useAuthStore();
  if (!user || !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export default function App() {
  const { setUser, setLoading } = useAuthStore();

  useEffect(() => {
    getMe()
      .then(({ user }) => setUser(user as Parameters<typeof setUser>[0]))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [setUser, setLoading]);

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/public" element={<PublicViewPage />} />

      {/* Protected */}
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/screening" element={<ScreeningPage />} />
        <Route path="/extraction/:studyId/:reviewId" element={<ExtractionPage />} />
        <Route path="/risk-of-bias/:studyId/:reviewId" element={<RiskOfBiasPage />} />
        <Route path="/import" element={
          <RequireRole roles={["ADMIN", "COORDINATOR"]}>
            <ImportPage />
          </RequireRole>
        } />
        <Route path="/admin" element={
          <RequireRole roles={["ADMIN", "COORDINATOR", "SUB_TEAM_LEADER"]}>
            <AdminPage />
          </RequireRole>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
